<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-aria/i18n';
import {ClassAPI, HeaderInfo, TypeContext, FunctionAPI, TypeLink} from '@react-spectrum/docs';
import packageData from '../package.json';

```tsx import
import {useCollator} from '@react-aria/i18n';
```

---
category: Internationalization
---

# useCollator

<p>{docs.exports.useCollator.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['useCollator']} />

## Introduction

`useCollator` wraps a builtin browser [Intl.Collator](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Collator)
object to provide a React Hook that integrates with the i18n system in React Aria. It handles string comparison according to the current locale,
updating when the locale changes, and caching of collators for performance. See the 
[Intl.Collator](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Collator) docs for 
information.

## API

<FunctionAPI function={docs.exports.useCollator} links={docs.links} />

## Example

This example includes two textfields and compares the values of the two fields using a collator according to the current locale.

```tsx example
function Example() {
  let [first, setFirst] = React.useState('');
  let [second, setSecond] = React.useState('');

  let collator = useCollator();
  let result = collator.compare(first, second);

  return (
    <>
      <div>
        <input 
          aria-label="First string"
          placeholder="First string"
          value={first}
          onChange={e => setFirst(e.target.value)} />
        <input 
          aria-label="Second string"
          placeholder="Second string"
          value={second}
          onChange={e => setSecond(e.target.value)} />
      </div>
      <p>
        {result === 0 
          ? 'The strings are the same' 
          : result < 0 
            ? 'First comes before second' 
            : 'Second comes before first'
        }
      </p>
    </>
  );
}
```
